import {
  ComputeElementStatsRequest,
  ComputeElementStatsResponse,
  ComputeScrollMapsStatsRequest,
  ComputeScrollMapsStatsResponse,
} from '@fullstory/proto/fs/services/alexa';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { createRequestCancellationAction, RequestDispatch } from '@fullstory/redux-util';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import { protoDeviceToUiDevice } from '@fullstory/search-converter/helpers';
import { isDataFresh } from '@fullstory/searchiecache';
import { getPromiseSettledResultValue } from '@fullstory/util/promise';
import { BatchOptions } from '@fullstory/util/xhr';
import { isEqual } from 'lodash';
import { AppDispatch } from '../../../types';
import {
  ComputeMetricResponseWithId,
  metricsCacheOptionsRefresh,
  MetricsRequestActions,
} from '../../metrics';
import { SearchGrpcRequestActions } from '../../search';
import { generateScrollMapEventSearch, modifyMetricToElementStats } from '../util';
import { HeatmapsActions, HeatmapsDataType, HeatmapsPayload } from './heatmapRequestActions';
import { UID } from '@fullstory/util/uid';

type UIDS = [UID, UID, UID, UID, UID, UID, UID];

export const HeatmapsAsyncActions = {
  appCache: (uids: UIDS, batchOptions?: BatchOptions) => ({
    action: createAppCacheRequestAction<HeatmapsDataType, HeatmapsPayload>()({
      query: async (
        dispatch: RequestDispatch<HeatmapsActions>,
        payload: HeatmapsPayload,
        force: boolean,
      ) => {
        const cacheOptions = force ? metricsCacheOptionsRefresh : payload.cacheOptions;

        const pageviewMetricsPromise = dispatch(
          MetricsRequestActions.legacyComputeMetric(
            {
              ...payload.pageviewMetric,
              cacheOptions,
            },
            payload.searchCacheKey,
            batchOptions,
            force,
            uids[0],
          ),
        );

        const timeOnPageMetricsPromise = dispatch(
          MetricsRequestActions.legacyComputeMetric(
            {
              ...payload.timeOnPageMetric,
              cacheOptions,
            },
            payload.searchCacheKey,
            batchOptions,
            force,
            uids[1],
          ),
        );

        const userMetricsPromise = payload.userMetricsBody
          ? dispatch(
              MetricsRequestActions.legacyComputeMetric(
                {
                  ...payload.userMetricsBody,
                  cacheOptions,
                },
                payload.searchCacheKey,
                batchOptions,
                force,
                uids[2],
              ),
            )
          : Promise.resolve(undefined);

        const medianScrollDepthMetricsPromise = dispatch(
          MetricsRequestActions.legacyComputeMetric(
            {
              ...payload.medianScrollDepthMetricsBody,
              cacheOptions,
            },
            payload.searchCacheKey,
            batchOptions,
            force,
            uids[3],
          ),
        );

        const allClickStatsPromise = payload.allClickMetricsBody
          ? dispatch(
              MetricsRequestActions.legacyComputeMetric(
                {
                  ...payload.allClickMetricsBody,
                  cacheOptions,
                },
                payload.searchCacheKey,
                batchOptions,
                force,
                uids[4],
              ),
            ).then(rsp =>
              modifyMetricToElementStats(
                rsp,
                ComputeElementStatsRequest.StatsType.STATS_TYPE_ALL_CLICKS,
              ),
            )
          : Promise.resolve(undefined);

        const clickTypeStatusPromise = payload.altClickTypeClickMetricsBody
          ? dispatch(
              MetricsRequestActions.legacyComputeMetric(
                {
                  ...payload.altClickTypeClickMetricsBody,
                  cacheOptions,
                },
                payload.searchCacheKey,
                batchOptions,
                force,
                uids[5],
              ),
            ).then(rsp => modifyMetricToElementStats(rsp, payload.clickType, payload.focusedOnlyOn))
          : allClickStatsPromise;

        let clickTypeStats: ComputeElementStatsResponse | undefined;
        let allClickStats: ComputeElementStatsResponse | undefined;
        let scrollResults: ComputeScrollMapsStatsResponse | undefined;
        let pageviewMetrics: ComputeMetricResponseWithId | undefined;
        let timeOnPageMetrics: ComputeMetricResponseWithId | undefined;
        let userMetrics: ComputeMetricResponseWithId | undefined;
        let medianScrollDepthMetrics: ComputeMetricResponseWithId | undefined;

        if (payload.pageDefId || payload.specId) {
          const eventSearch = generateScrollMapEventSearch({
            behavior: payload.behavior,
            constraints: payload.constraints,
            scopedSegmentDefinition: payload.scopedSegmentDefinition,
            addSelectForPageDef: payload.joinOnPageViewCriteriaForScrollMaps,
          });

          const scrollResultsPromise = dispatch(
            SearchGrpcRequestActions.scrollMapsFacet(
              '',
              protoDeviceToUiDevice(payload.deviceType),
              ComputeScrollMapsStatsRequest.StatsType.STATS_TYPE_SCROLL_DEPTH_COUNTS,
              payload.scopedSegmentDefinition,
              1000, // top 1000 clicks
              payload.orgId,
              0, // not using page cluster
              force,
              undefined,
              undefined,
              undefined,
              eventSearch,
              payload.pageDefId,
              payload.specId,
              uids[6],
            ),
          );

          await Promise.allSettled([
            pageviewMetricsPromise,
            allClickStatsPromise,
            clickTypeStatusPromise,
            scrollResultsPromise,
            timeOnPageMetricsPromise,
            userMetricsPromise,
            medianScrollDepthMetricsPromise,
          ])
            .then(results => {
              pageviewMetrics = getPromiseSettledResultValue(results[0]);
              allClickStats = getPromiseSettledResultValue(results[1]);
              clickTypeStats = getPromiseSettledResultValue(results[2]);
              scrollResults = getPromiseSettledResultValue(results[3]);
              timeOnPageMetrics = getPromiseSettledResultValue(results[4]);
              userMetrics = getPromiseSettledResultValue(results[5]);
              medianScrollDepthMetrics = getPromiseSettledResultValue(results[6]);
            })
            .catch(e => console.log(e));
        }

        return {
          pageviewMetrics,
          userMetrics,
          timeOnPageMetrics,
          medianScrollDepthMetrics,
          allClickStats,
          clickTypeStats,
          scrollResults,
          computedAt: timeOnPageMetrics?.computedAt || null,
        };
      },
      loading: HeatmapsActions.appCacheIsLoading,
      failed: HeatmapsActions.appCacheLoadFailed,
      refetching: HeatmapsActions.appCacheIsRefetching,
      hasData: HeatmapsActions.appCacheHasData,
      fresh: HeatmapsActions.appCacheFresh,
      isDataFresh: (payload: HeatmapsPayload, data: HeatmapsDataType) =>
        isDataFresh(payload.cacheOptions, Timestamp.mustDate(data.computedAt), new Date()),
      isSimiliar: (newData: HeatmapsDataType, oldData: HeatmapsDataType) => {
        if (!oldData || !newData) return false;
        return isEqual(oldData, newData);
      },
    }),
    cancel: (dispatch: AppDispatch, uids: UIDS) => {
      uids.forEach(id => dispatch(createRequestCancellationAction(id)));
    },
  }),
};
